import { FC, ReactNode, useEffect, useId, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Input, Modal } from 'antd';
import type { ModalProps } from 'antd';
import { TbArrowRight, TbTrash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformAlert } from '@/shared/components/alerts';
import { toast } from '@/shared/components/toasts/PlatformToast';

export type DestructiveActionSeverity = 'warning' | 'danger';

export interface DestructiveActionModalProps {
    actionLabel: string;
    alertDescription: ReactNode;
    alertTitle?: ReactNode;
    children?: ReactNode;
    confirmationLabel?: ReactNode;
    confirmationValue?: string;
    errorMessage?: string;
    loading?: boolean;
    onCancel: () => void;
    onConfirm: () => Promise<unknown> | unknown;
    open: boolean;
    severity?: DestructiveActionSeverity;
    title: ReactNode;
    width?: ModalProps['width'];
}

/**
 * Shared confirmation contract for destructive platform actions.
 * Reversible actions intentionally use warning treatment without typed friction;
 * permanent actions can require an explicit, case-sensitive confirmation value.
 */
const DestructiveActionModal: FC<DestructiveActionModalProps> = ({
    actionLabel,
    alertDescription,
    alertTitle,
    children,
    confirmationLabel,
    confirmationValue,
    errorMessage = 'Unable to complete this action',
    loading = false,
    onCancel,
    onConfirm,
    open,
    severity = 'danger',
    title,
    width = 'min(30rem, calc(100vw - 2rem))',
}) => {
    const { token } = useAntDesignTheme();
    const confirmationInputId = useId();
    const [confirmation, setConfirmation] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const busy = loading || submitting;
    const confirmed = confirmationValue === undefined || confirmation === confirmationValue;

    useEffect(() => {
        if (!open) {
            setConfirmation('');
            setSubmitting(false);
        }
    }, [open]);

    const close = () => {
        if (!busy) {
            setConfirmation('');
            onCancel();
        }
    };

    const confirm = async () => {
        if (!confirmed || busy) {
            return;
        }

        try {
            setSubmitting(true);
            await onConfirm();
            setConfirmation('');
            onCancel();
        } catch (error) {
            captureException(error, { tags: { action: 'destructive-modal-confirm' } });
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            destroyOnHidden
            footer={
                <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
                    <Button disabled={busy} onClick={close}>
                        Cancel
                    </Button>
                    <Button
                        danger={severity === 'danger'}
                        disabled={!confirmed || busy}
                        loading={busy}
                        type='primary'
                        onClick={() => void confirm()}
                    >
                        {actionLabel}
                        <TbArrowRight size='1rem' />
                    </Button>
                </div>
            }
            keyboard={!busy}
            maskClosable={!busy}
            onCancel={close}
            open={open}
            title={title}
            width={width}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: token.margin }}>
                {children}
                <PlatformAlert
                    description={alertDescription}
                    showIcon
                    status={severity === 'danger' ? 'error' : 'warning'}
                    title={
                        alertTitle ??
                        (severity === 'danger' ? 'This action is permanent' : 'This action moves the item to Trash')
                    }
                />
                {confirmationValue !== undefined ? (
                    <div>
                        <label
                            htmlFor={confirmationInputId}
                            style={{ color: token.colorTextSecondary, display: 'block', marginBottom: token.marginXS }}
                        >
                            <small>
                                {confirmationLabel ?? (
                                    <>
                                        Type <code style={{ userSelect: 'all' }}>{confirmationValue}</code> to confirm.
                                    </>
                                )}
                            </small>
                        </label>
                        <Input
                            autoComplete='off'
                            disabled={busy}
                            id={confirmationInputId}
                            placeholder={confirmationValue}
                            prefix={<TbTrash size='1rem' style={{ color: token.colorTextTertiary }} />}
                            value={confirmation}
                            onChange={(event) => setConfirmation(event.target.value)}
                            onPressEnter={() => void confirm()}
                        />
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};

export default DestructiveActionModal;
